/**
 * LOS MEDICAMENTOS DE LA FICHA (Lissette 2026-10-15).
 *
 * *"Un buscador de medicina y poder guardarlo en los clientes, es como escribir
 * los medicamentos que toma pero que el sistema lo reconozca, se lo complete y
 * le dé sugerencias, y también de dosis."*
 *
 * Vive en la pestaña "Médicos y citas", entre los médicos y las citas, y con
 * las mismas decisiones que los médicos (ver `MedicosTab.jsx`):
 *   · POR PERSONA de la casa — titular, cónyuge y cada dependiente —, y las
 *     personas sin medicamentos salen igual, para ver a quién le falta.
 *   · Lo ve todo el que puede abrir la ficha; el detalle va cifrado en la base.
 *   · Un medicamento cuya persona ya no está en la casa NO se esconde: sale en
 *     un grupo aparte con el aviso, para poder moverlo o quitarlo.
 *
 * EL BUSCADOR: al escribir "metf" pregunta al catálogo de la Biblioteca
 * Nacional de Medicina (a través del servidor) y sugiere "metFORMIN (Oral
 * Pill)", "Glucophage"... Al elegir uno, enseña las dosis que existen de verdad
 * ("500 mg Tab", "850 mg Tab") para escoger con un clic. Nada de esto es
 * obligatorio: si no aparece, o el catálogo no contesta, se escribe a mano.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Input, Textarea, Select, confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';
import { fmt } from '../utils.js';
import { nombreDePersona } from '../personasDelHogar.js';
import {
  CAMPOS_DEL_MEDICAMENTO, LARGO_MAXIMO, ETIQUETAS, FRECUENCIAS,
  medicamentoVacio, medicamentoComoFormulario, resumenDelMedicamento,
  aplicarSugerencia, aplicarDosis, escribirNombre, escribirDosis,
} from '../medicamentosDelCliente.js';
import {
  listClientMedications, createClientMedication, updateClientMedication, deleteClientMedication,
  buscarMedicamentos,
} from '../api.js';

export default function MedicamentosDelCliente({ client, personas, medicos = [], lang = 'es' }) {
  const es = lang !== 'en';
  const clientId = client?.id;
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [errorDeCarga, setErrorDeCarga] = useState('');
  const [form, setForm] = useState(null);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    if (!clientId) return;
    setCargando(true); setErrorDeCarga('');
    try { setLista(await listClientMedications(clientId)); }
    catch (e) { setErrorDeCarga(e.message || (es ? 'No se pudieron cargar los medicamentos' : 'Could not load medications')); }
    finally { setCargando(false); }
  };
  useEffect(() => { cargar(); }, [clientId]);

  const grupos = useMemo(() => {
    const porPersona = new Map();
    for (const m of lista) {
      const k = m.persona || 'primary';
      if (!porPersona.has(k)) porPersona.set(k, []);
      porPersona.get(k).push(m);
    }
    const salida = personas.map((p) => ({ ...p, items: porPersona.get(p.key) || [] }));
    const conocidas = new Set(personas.map((p) => p.key));
    for (const [k, items] of porPersona) {
      if (conocidas.has(k)) continue;
      salida.push({
        key: k, nombre: nombreDePersona(client, k, lang),
        parentesco: es ? 'Se quitó del hogar' : 'Removed from household',
        huerfana: true, items,
      });
    }
    return salida;
  }, [lista, personas, client, lang, es]);

  const abrirNuevo = (persona) => { setEditando(null); setForm(medicamentoVacio(persona)); };
  const abrirEdicion = (m) => { setEditando(m); setForm(medicamentoComoFormulario(m)); };
  const cerrar = () => { setForm(null); setEditando(null); };

  const guardar = async () => {
    if (!form || guardando) return;
    if (!String(form.nombre || '').trim()) {
      toast.error(es ? 'Escribe el nombre del medicamento' : 'Enter the medication name');
      return;
    }
    setGuardando(true);
    try {
      const payload = { persona: form.persona };
      for (const c of CAMPOS_DEL_MEDICAMENTO) payload[c] = form[c] || '';
      const r = editando
        ? await updateClientMedication(clientId, editando.id, payload)
        : await createClientMedication(clientId, payload);
      await cargar();
      cerrar();
      if (r?.recortados?.length) {
        const nombres = r.recortados.map((c) => (es ? ETIQUETAS[c]?.es : ETIQUETAS[c]?.en) || c).join(', ');
        toast.error(es ? `Guardado, pero se acortó por ser muy largo: ${nombres}` : `Saved, but trimmed for length: ${nombres}`);
      } else {
        toast.success(es ? 'Medicamento guardado' : 'Medication saved');
      }
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo guardar' : 'Save failed'));
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (m) => {
    const ok = await confirmDialog({
      title: es ? '¿Quitar este medicamento?' : 'Remove this medication?',
      message: (m.nombre || '') + (es ? ' — se quita de la ficha.' : ' — removed from the record.'),
      confirmLabel: es ? 'Quitar' : 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteClientMedication(clientId, m.id);
      await cargar();
      toast.success(es ? 'Medicamento quitado' : 'Medication removed');
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo quitar' : 'Delete failed'));
    }
  };

  return (
    <div style={{ marginTop: '1.4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem', marginBottom: '.6rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '.92rem', color: 'var(--c-white,#eef6ff)' }}>
            💊 {es ? 'Medicamentos' : 'Medications'}
          </div>
          <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
            {es
              ? 'Lo que toma cada persona de la casa, con su dosis. Escribe el nombre y Deskte te lo sugiere del catálogo oficial.'
              : 'What each household member takes, with the dose. Type the name and Deskte suggests it from the official catalog.'}
          </div>
        </div>
        {!cargando && !!lista.length && (
          <span style={{ fontSize: '.65rem', padding: '.2rem .6rem', borderRadius: 100, background: 'rgba(0,212,255,.08)', border: '1px solid rgba(0,212,255,.2)', color: 'var(--c-cyan,#00d4ff)', fontWeight: 700 }}>
            💊 {lista.length} {es ? (lista.length === 1 ? 'medicamento' : 'medicamentos') : (lista.length === 1 ? 'medication' : 'medications')}
          </span>
        )}
      </div>

      {cargando && (
        <div style={{ textAlign: 'center', padding: '1.2rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>
          {es ? 'Cargando…' : 'Loading…'}
        </div>
      )}

      {!!errorDeCarga && !cargando && (
        <div style={{ padding: '.75rem 1rem', marginBottom: '.6rem', borderRadius: 10, background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', color: 'var(--c-red,#ff3d6b)', fontSize: '.78rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem' }}>
          <span>{errorDeCarga}</span>
          <button type="button" onClick={cargar} className="dkt-btn-s" style={{ fontSize: '.72rem', padding: '.3rem .75rem' }}>
            {es ? 'Reintentar' : 'Retry'}
          </button>
        </div>
      )}

      {!cargando && !errorDeCarga && grupos.map((g) => (
        <div key={g.key} style={{ marginBottom: '.7rem', background: 'var(--c-surface2,#112538)', border: `1px solid ${g.huerfana ? 'rgba(255,176,32,.3)' : 'var(--c-border,#1a3550)'}`, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '.5rem .85rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.6rem', borderBottom: g.items.length ? '1px solid rgba(26,53,80,.6)' : 'none', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontWeight: 700, fontSize: '.82rem', color: g.huerfana ? 'var(--c-amber,#ffb700)' : 'var(--c-white,#eef6ff)' }}>
                {g.huerfana ? '⚠️ ' : ''}{g.nombre}
              </span>
              <span style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--font-mono)', marginLeft: '.5rem' }}>
                {g.parentesco}
              </span>
              {!g.items.length && (
                <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginLeft: '.6rem' }}>
                  · {es ? 'sin medicamentos puestos' : 'no medications on file'}
                </span>
              )}
            </div>
            {!g.huerfana && (
              <button type="button" onClick={() => abrirNuevo(g.key)} className="dkt-btn-s" style={{ fontSize: '.72rem', padding: '.3rem .7rem' }}>
                + {es ? 'Medicamento' : 'Medication'}
              </button>
            )}
          </div>

          {g.items.map((m) => (
            <div key={m.id} style={{ padding: '.55rem .85rem', borderTop: '1px solid rgba(26,53,80,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '.6rem', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '.84rem', color: 'var(--c-white,#eef6ff)' }}>
                  💊 {m.nombre || (es ? '(sin nombre)' : '(no name)')}
                </div>
                {!!resumenDelMedicamento(m) && (
                  <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2 }}>{resumenDelMedicamento(m)}</div>
                )}
                {!!m.recetadoPor && (
                  <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2 }}>
                    🩺 {es ? 'Recetado por' : 'Prescribed by'} {m.recetadoPor}
                  </div>
                )}
                {!!m.notas && (
                  <div style={{ fontSize: '.74rem', color: 'var(--c-text,#c8dff0)', marginTop: '.3rem', whiteSpace: 'pre-wrap' }}>{m.notas}</div>
                )}
                <div style={{ fontSize: '.63rem', color: 'var(--c-text3,#45688a)', marginTop: '.35rem', fontFamily: 'var(--font-mono)' }}>
                  ✍️ {es ? 'Escrito por' : 'Written by'} {m.creadoPor || (es ? 'alguien de la agencia' : 'someone at the agency')} · {fmt(m.createdAt)}
                  {m.updatedAt && new Date(m.updatedAt) - new Date(m.createdAt) > 60000 && (
                    <> · {es ? 'actualizado por' : 'updated by'} {m.actualizadoPor || '—'} · {fmt(m.updatedAt)}</>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '.35rem', flexShrink: 0 }}>
                <button type="button" onClick={() => abrirEdicion(m)} className="dkt-btn-s" style={{ fontSize: '.68rem', padding: '.25rem .6rem' }}>
                  {es ? 'Editar' : 'Edit'}
                </button>
                <button type="button" onClick={() => borrar(m)}
                  style={{ background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.25rem .6rem', cursor: 'pointer', fontSize: '.68rem', fontWeight: 700, color: 'var(--c-red,#ff3d6b)' }}>
                  {es ? 'Quitar' : 'Remove'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {!!form && (
        <FormularioDeMedicamento
          titulo={`💊 ${editando ? (es ? 'Editar medicamento' : 'Edit medication') : (es ? 'Nuevo medicamento' : 'New medication')}`}
          form={form} setForm={setForm} personas={personas} medicos={medicos} client={client}
          lang={lang} guardando={guardando} onGuardar={guardar} onCerrar={cerrar} />
      )}
    </div>
  );
}

function FormularioDeMedicamento({ titulo, form, setForm, personas, medicos, client, lang, guardando, onGuardar, onCerrar }) {
  const es = lang !== 'en';
  // Las dosis que existen del medicamento elegido de la búsqueda. Vacío cuando
  // se escribió a mano o se abrió uno ya guardado: no se inventan.
  const [presentaciones, setPresentaciones] = useState([]);
  const medicosDeLaCasa = useMemo(
    () => [...new Set((medicos || []).map((m) => String(m.nombre || '').trim()).filter(Boolean))],
    [medicos],
  );

  return (
    <Modal title={titulo} onClose={onCerrar} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
        <div style={{ gridColumn: 'span 2' }}>
          <Select label={es ? '¿Quién lo toma?' : 'Who takes it?'}
            value={form.persona} onChange={(e) => setForm((f) => ({ ...f, persona: e.target.value }))}>
            {personas.map((p) => (
              <option key={p.key} value={p.key}>{p.nombre} — {p.parentesco}</option>
            ))}
            {!personas.some((p) => p.key === form.persona) && (
              <option value={form.persona}>{nombreDePersona(client, form.persona, lang)}</option>
            )}
          </Select>
        </div>

        <div style={{ gridColumn: 'span 2' }}>
          <BuscadorDeMedicamento
            valor={form.nombre} lang={lang}
            onEscribir={(texto) => { setForm((f) => escribirNombre(f, texto)); setPresentaciones([]); }}
            onElegir={(s) => { setForm((f) => aplicarSugerencia(f, s)); setPresentaciones(s.presentaciones || []); }} />
        </div>

        <div style={{ gridColumn: 'span 2' }}>
          <Input label={es ? ETIQUETAS.dosis.es : ETIQUETAS.dosis.en} maxLength={LARGO_MAXIMO.dosis}
            value={form.dosis} placeholder={es ? '500 mg Tab' : '500 mg Tab'}
            onChange={(e) => setForm((f) => escribirDosis(f, e.target.value))} />
          {presentaciones.length > 1 && (
            <div style={{ marginTop: '.35rem' }}>
              <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginBottom: '.25rem' }}>
                {es ? 'Dosis que existen de este medicamento — toca una:' : 'Doses that exist for this medication — tap one:'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.3rem' }}>
                {presentaciones.map((p) => {
                  const activa = form.dosis === p.dosis;
                  return (
                    <button key={p.dosis} type="button" onClick={() => setForm((f) => aplicarDosis(f, p))}
                      style={{ fontSize: '.7rem', padding: '.22rem .55rem', borderRadius: 100, cursor: 'pointer', fontFamily: 'inherit',
                        border: `1px solid ${activa ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`,
                        background: activa ? 'rgba(0,212,255,.15)' : 'var(--c-surface3,#162d44)',
                        color: activa ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text,#c8dff0)', fontWeight: activa ? 700 : 500 }}>
                      {p.dosis}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div>
          <Input label={es ? ETIQUETAS.frecuencia.es : ETIQUETAS.frecuencia.en} maxLength={LARGO_MAXIMO.frecuencia}
            list="dkt-frecuencias-med" value={form.frecuencia}
            placeholder={es ? '1 vez al día' : 'Once a day'}
            onChange={(e) => setForm((f) => ({ ...f, frecuencia: e.target.value }))} />
          <datalist id="dkt-frecuencias-med">
            {(es ? FRECUENCIAS.es : FRECUENCIAS.en).map((x) => <option key={x} value={x} />)}
          </datalist>
        </div>
        <div>
          {/* Los médicos ya puestos en la ficha salen como sugerencia. */}
          <Input label={es ? ETIQUETAS.recetadoPor.es : ETIQUETAS.recetadoPor.en} maxLength={LARGO_MAXIMO.recetadoPor}
            list="dkt-medicos-med" value={form.recetadoPor}
            placeholder={es ? 'Dr. Juan Pérez' : 'Dr. Jane Doe'}
            onChange={(e) => setForm((f) => ({ ...f, recetadoPor: e.target.value }))} />
          <datalist id="dkt-medicos-med">
            {medicosDeLaCasa.map((x) => <option key={x} value={x} />)}
          </datalist>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <Textarea label={es ? ETIQUETAS.notas.es : ETIQUETAS.notas.en} maxLength={LARGO_MAXIMO.notas}
            value={form.notas}
            placeholder={es ? 'Con comida, lo compra en Walgreens, le sale caro…' : 'With food, buys it at Walgreens, expensive…'}
            onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '.5rem', marginTop: '.9rem' }}>
        <button type="button" onClick={onGuardar} disabled={guardando} className="dkt-btn-p" style={{ flex: 1, opacity: guardando ? 0.6 : 1 }}>
          {guardando ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}
        </button>
        <button type="button" onClick={onCerrar} className="dkt-btn-s" style={{ flex: 1 }}>
          {es ? 'Cancelar' : 'Cancel'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * El campo del nombre con sugerencias. Pregunta al servidor después de una
 * pausa al teclear (no en cada letra) y solo enseña la respuesta de la ÚLTIMA
 * pregunta: una respuesta lenta de "met" no puede pisar la de "metfor".
 */
export function BuscadorDeMedicamento({ valor, onEscribir, onElegir, lang }) {
  const es = lang !== 'en';
  const [sugerencias, setSugerencias] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [sinServicio, setSinServicio] = useState(false);
  const [marcada, setMarcada] = useState(-1);
  const ultima = useRef(0);
  const pausa = useRef(null);

  useEffect(() => () => clearTimeout(pausa.current), []);

  const escribir = (texto) => {
    onEscribir(texto);
    clearTimeout(pausa.current);
    const q = texto.trim();
    if (q.length < 2) { setSugerencias([]); setAbierto(false); setBuscando(false); return; }
    setBuscando(true);
    pausa.current = setTimeout(async () => {
      const n = ++ultima.current;
      const r = await buscarMedicamentos(q);
      if (n !== ultima.current) return;
      setBuscando(false);
      setSinServicio(r.unavailable);
      setSugerencias(r.data);
      setMarcada(-1);
      setAbierto(true);
    }, 300);
  };

  const elegir = (s) => {
    clearTimeout(pausa.current);
    ultima.current += 1;
    setAbierto(false);
    setSugerencias([]);
    setBuscando(false);
    onElegir(s);
  };

  const teclas = (e) => {
    if (!abierto || !sugerencias.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setMarcada((i) => Math.min(i + 1, sugerencias.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMarcada((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && marcada >= 0) { e.preventDefault(); elegir(sugerencias[marcada]); }
    else if (e.key === 'Escape') { setAbierto(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Input label={`${es ? ETIQUETAS.nombre.es : ETIQUETAS.nombre.en} *`} maxLength={LARGO_MAXIMO.nombre}
        value={valor} autoComplete="off"
        placeholder={es ? 'Escribe: metformina, Eliquis, insulina…' : 'Type: metformin, Eliquis, insulin…'}
        onChange={(e) => escribir(e.target.value)} onKeyDown={teclas}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
        onFocus={() => { if (sugerencias.length) setAbierto(true); }} />
      <div style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)', marginTop: 3, minHeight: '1em' }}>
        {buscando
          ? (es ? 'Buscando en el catálogo…' : 'Searching the catalog…')
          : sinServicio
            ? (es ? 'El catálogo no contesta ahora; escríbelo a mano y guárdalo igual.' : 'The catalog is not answering; type it by hand and save anyway.')
            : (es ? 'Catálogo oficial de la Biblioteca Nacional de Medicina (EE. UU.). Si no aparece, escríbelo a mano.' : 'Official U.S. National Library of Medicine catalog. If it is not there, type it by hand.')}
      </div>
      {abierto && (
        <div style={{ position: 'absolute', top: '3.6rem', left: 0, right: 0, zIndex: 50, maxHeight: 280, overflowY: 'auto',
          background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, boxShadow: '0 12px 30px rgba(0,0,0,.45)' }}>
          {!sugerencias.length && (
            <div style={{ padding: '.6rem .75rem', fontSize: '.74rem', color: 'var(--c-text3,#45688a)' }}>
              {sinServicio
                ? (es ? 'Sin respuesta del catálogo.' : 'No answer from the catalog.')
                : (es ? 'No aparece en el catálogo. Puedes guardarlo como lo escribiste.' : 'Not in the catalog. You can save it as typed.')}
            </div>
          )}
          {sugerencias.map((s, i) => (
            <button key={s.nombre} type="button" onMouseDown={(e) => { e.preventDefault(); elegir(s); }}
              onMouseEnter={() => setMarcada(i)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '.45rem .75rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                background: i === marcada ? 'rgba(0,212,255,.1)' : 'transparent', borderBottom: '1px solid rgba(26,53,80,.45)' }}>
              <div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>💊 {s.nombre}</div>
              {!!s.presentaciones?.length && (
                <div style={{ fontSize: '.66rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.presentaciones.slice(0, 5).map((p) => p.dosis).join(' · ')}{s.presentaciones.length > 5 ? ` · +${s.presentaciones.length - 5}` : ''}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
